import React from 'react';
import { ArrowLeft, Shield, Zap, Lock, Server, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { LEGAL_ENTITY, getSubProcessors } from '../config/legalEntity';

const PlatformNotice = () => {
  const navigate = useNavigate();

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="platform-notice-page">
      <Button
        variant="ghost"
        onClick={() => navigate(-1)}
        className="mb-6 text-slate-600 hover:text-slate-900"
        data-testid="platform-notice-back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>

      <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8 space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Platform Privacy Notice
          </h1>
          <p className="text-sm text-slate-500 mt-1">ApplyDrive.uk — How we handle your data</p>
          <p className="text-sm text-slate-500">Last updated: May 2026</p>
        </div>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">What is ApplyDrive.uk?</h2>
          <p className="text-slate-600 leading-relaxed">
            <strong>ApplyDrive</strong> is a UK technology platform that powers vehicle finance application forms on behalf of authorised, FCA-regulated finance brokers and dealers. We provide the technology — we do not make lending decisions, provide financial advice, or act as a credit broker.
          </p>
          <p className="text-slate-600 leading-relaxed">
            ApplyDrive is a trading name of <strong>{LEGAL_ENTITY.companyName}</strong>, a company registered in {LEGAL_ENTITY.jurisdiction} (Company No.{' '}
            <a href={LEGAL_ENTITY.companiesHouseUrl} target="_blank" rel="noopener noreferrer" className="text-purple-700 hover:underline">{LEGAL_ENTITY.companyNumber}</a>) with registered office at {LEGAL_ENTITY.registeredOffice}. We are registered with the UK Information Commissioner's Office (ICO Registration{' '}
            <a href={LEGAL_ENTITY.icoRegisterUrl} target="_blank" rel="noopener noreferrer" className="text-purple-700 hover:underline">{LEGAL_ENTITY.icoNumber}</a>).
          </p>
          <p className="text-slate-600 leading-relaxed">
            The <strong>data controller</strong> for your application is the finance broker or dealer whose branded portal you are using. Their privacy policy governs how your personal data is collected, stored, and shared. This notice explains only how {LEGAL_ENTITY.companyName} handles your data as it passes through our platform.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-slate-800">How Your Data Flows</h2>
          <div className="grid gap-4">
            <div className="flex gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">1. You enter your details</h3>
                <p className="text-sm text-slate-600 mt-1">Your data is entered directly into the form in your browser. If you use our document scanning feature, your documents are processed in real-time to extract relevant information and are not retained.</p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Lock className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">2. Secure transmission</h3>
                <p className="text-sm text-slate-600 mt-1">When you submit your application, your data is transmitted over an encrypted (HTTPS/TLS) connection directly to the broker's finance proposal system.</p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Server className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">3. Ephemeral processing</h3>
                <p className="text-sm text-slate-600 mt-1">ApplyDrive.uk processes your application data <strong>in transit only</strong>. We transform and validate the data to ensure it meets the requirements of the broker's proposal system. Your personal data is not stored on our servers after submission.</p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">4. No long-term retention</h3>
                <p className="text-sm text-slate-600 mt-1">We do not maintain a database of applicant personal data. Anonymised usage analytics (e.g. submission counts, step completion rates) may be retained to improve the platform, but these contain no personally identifiable information.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">Document Scanning (OCR)</h2>
          <p className="text-slate-600 leading-relaxed">
            If you use our document scanning feature to auto-fill your application, the text or image you provide is sent to a secure AI processing service to extract relevant fields (name, address, employment details, etc.). This processing happens in real-time and the original document data is not stored by ApplyDrive.uk or the AI service after extraction is complete.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">Third Parties Used</h2>
          <p className="text-slate-600 leading-relaxed">
            In the course of processing your application, our platform uses the following sub-processors. This is disclosed in line with UK GDPR Art. 28 transparency requirements. Each receives only the minimum data needed to perform its function and is bound by appropriate data protection agreements.
          </p>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-700">Sub-processor</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-700">Role</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-700">Data Residency</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {getSubProcessors().map((sp) => (
                  <tr key={sp.name}>
                    <td className="px-3 py-2 text-slate-700 font-medium align-top">{sp.name}</td>
                    <td className="px-3 py-2 text-slate-600 align-top">{sp.role}</td>
                    <td className="px-3 py-2 text-slate-600 align-top">{sp.dataResidency}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500">
            Your broker's privacy policy provides full details of all parties involved in processing your application (including lenders and credit reference agencies).
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">Security</h2>
          <div className="flex gap-4 p-4 bg-green-50 rounded-xl border border-green-100">
            <Shield className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-600 space-y-2">
              <p>All data transmitted through ApplyDrive.uk is encrypted using industry-standard TLS encryption.</p>
              <p>Our platform infrastructure is hosted on secure, UK/EU-based cloud servers with appropriate technical and organisational security measures in place.</p>
              <p>Access to platform systems is restricted to authorised personnel only.</p>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">Cookies</h2>
          <p className="text-slate-600 leading-relaxed">
            ApplyDrive.uk uses only essential cookies required for the application form to function correctly (e.g. maintaining your session while completing the form). We do not use tracking, advertising, or analytics cookies that identify individual users.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">Your Rights</h2>
          <p className="text-slate-600 leading-relaxed">
            As ApplyDrive does not retain your personal data, data subject access requests (SARs), deletion requests, and other rights under UK GDPR should be directed to your broker — the data controller — whose contact details are provided in their privacy policy and on the application form.
          </p>
          <p className="text-slate-600 leading-relaxed">
            If you have questions specifically about how ApplyDrive processes data as a technology platform, you can contact us at <a href={`mailto:${LEGAL_ENTITY.privacyEmail}`} className="text-purple-700 hover:underline">{LEGAL_ENTITY.privacyEmail}</a>.
          </p>
          <p className="text-slate-600 leading-relaxed">
            You also have the right to lodge a complaint with the Information Commissioner's Office (ICO). {LEGAL_ENTITY.companyName} is registered with the ICO under registration number{' '}
            <a href={LEGAL_ENTITY.icoRegisterUrl} target="_blank" rel="noopener noreferrer" className="text-purple-700 hover:underline">{LEGAL_ENTITY.icoNumber}</a>.
          </p>
        </section>

        <div className="border-t border-slate-200 pt-6 mt-6 text-center space-y-2">
          <p className="text-xs text-slate-500 leading-relaxed">
            {LEGAL_ENTITY.tradingName} is a trading name of <strong>{LEGAL_ENTITY.companyName}</strong>, a company registered in {LEGAL_ENTITY.jurisdiction} (Company No.{' '}
            <a href={LEGAL_ENTITY.companiesHouseUrl} target="_blank" rel="noopener noreferrer" className="text-purple-700 hover:underline">{LEGAL_ENTITY.companyNumber}</a>). Registered office: {LEGAL_ENTITY.registeredOffice}. ICO Registration:{' '}
            <a href={LEGAL_ENTITY.icoRegisterUrl} target="_blank" rel="noopener noreferrer" className="text-purple-700 hover:underline">{LEGAL_ENTITY.icoNumber}</a>.
          </p>
          <p className="text-xs text-slate-400">Powered by ApplyDrive.uk — Vehicle finance applications, simplified.</p>
        </div>
      </div>
    </div>
  );
};

export default PlatformNotice;
